import { useState, useEffect } from 'react'
import type { WizardState, Renter, LejerManuel } from './types'
import { apiFetch } from '../../../lib/api-client'

interface LeaseContact {
  id: string
  contact_type: string
  contact_person: string | null
  email: string | null
  phone: string | null
}
import { SectionBlock, inputStyle, BLUE } from './ui'

const COUNTRY_CODES = [
  { flag: '🇩🇰', code: '+45', label: 'Danmark' },
  { flag: '🇸🇪', code: '+46', label: 'Sverige' },
  { flag: '🇳🇴', code: '+47', label: 'Norge' },
  { flag: '🇩🇪', code: '+49', label: 'Tyskland' },
  { flag: '🇬🇧', code: '+44', label: 'UK' },
]

const LANDE = ['Danmark', 'Sverige', 'Norge', 'Tyskland', 'UK', 'USA', 'Andet']

const EMPTY_LEJER: LejerManuel = {
  firstname: '', lastname: '', email: '', phone: '', phone_country: '+45',
  address: '', address2: '', postnr: '', by: '', land: 'Danmark',
  cpr: '', reg_nr: '', konto_nr: '',
}

const IconPerson = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
)

const IconPersonAdd = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
)

const IconSend = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)

function Toggle({ label, value, onChange, disabled, comingSoon }: {
  label: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean; comingSoon?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: disabled ? '#9ca3af' : '#111827', fontWeight: 500 }}>{label}</span>
        {comingSoon && (
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#f3f4f6', color: '#9ca3af', fontWeight: 500 }}>
            Kommer snart
          </span>
        )}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(!value)}
        style={{
          width: 40, height: 22, borderRadius: 11, border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          background: value && !disabled ? BLUE : '#d1d5db',
          position: 'relative', flexShrink: 0, opacity: disabled ? 0.5 : 1,
          transition: 'background 0.15s',
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: value && !disabled ? 20 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s', display: 'block',
        }} />
      </button>
    </div>
  )
}

// ─── Phone field with flag ────────────────────────────────────────────────────
function PhoneField({ value, countryCode, onPhone, onCountry }: {
  value: string; countryCode: string; onPhone: (v: string) => void; onCountry: (v: string) => void
}) {
  const selected = COUNTRY_CODES.find(c => c.code === countryCode) ?? COUNTRY_CODES[0]
  return (
    <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #d1d5db' }}>
      <div style={{ display: 'flex', alignItems: 'center', background: '#f9fafb', borderRight: '1px solid #d1d5db', flexShrink: 0, position: 'relative' }}>
        <span style={{ paddingLeft: 10, fontSize: 18, lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>
          {selected.flag}
        </span>
        <select
          value={countryCode}
          onChange={e => onCountry(e.target.value)}
          style={{
            appearance: 'none', border: 'none', background: 'transparent',
            padding: '9px 26px 9px 6px', fontSize: 13, cursor: 'pointer', color: '#374151',
            outline: 'none',
          }}
        >
          {COUNTRY_CODES.map(c => (
            <option key={c.code} value={c.code}>{c.code}</option>
          ))}
        </select>
        <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af', fontSize: 10 }}>▼</div>
      </div>
      <input
        style={{ ...inputStyle, border: 'none', borderRadius: 0, flex: 1 }}
        type="tel"
        value={value}
        onChange={e => onPhone(e.target.value)}
        placeholder={`${selected.code} 12 34 56 78`}
      />
    </div>
  )
}

// ─── Lejer card label ─────────────────────────────────────────────────────────
function LejerLabel({ num, isPrimary }: { num: number; isPrimary: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 12px' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
        <IconPerson />
      </div>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Lejer #{num}</span>
      {isPrimary && (
        <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: '#eff6ff', color: BLUE, fontWeight: 600 }}>
          Primær
        </span>
      )}
    </div>
  )
}

// ─── Manuel lejer form ────────────────────────────────────────────────────────
function ManuelLejerForm({ data, num, onChange, onRemove }: {
  data: LejerManuel; num: number; onChange: (d: LejerManuel) => void; onRemove?: () => void
}) {
  const upd = (patch: Partial<LejerManuel>) => onChange({ ...data, ...patch })

  return (
    <div style={{ border: '1px solid #e8eaed', borderRadius: 8, padding: '14px 16px', marginBottom: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <LejerLabel num={num} isPrimary={num === 1} />
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
          >×</button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Fornavn <span style={{ color: '#dc2626' }}>*</span></div>
          <input style={inputStyle} value={data.firstname} onChange={e => upd({ firstname: e.target.value })} placeholder="Fornavn" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Efternavn <span style={{ color: '#dc2626' }}>*</span></div>
          <input style={inputStyle} value={data.lastname} onChange={e => upd({ lastname: e.target.value })} placeholder="Efternavn" />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>E-mail <span style={{ color: '#dc2626' }}>*</span></div>
        <input style={inputStyle} type="email" value={data.email} onChange={e => upd({ email: e.target.value })} placeholder="lejer@email.dk" />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Telefon</div>
        <PhoneField
          value={data.phone}
          countryCode={data.phone_country}
          onPhone={v => upd({ phone: v })}
          onCountry={v => upd({ phone_country: v })}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Nuværende adresse</div>
        <input style={{ ...inputStyle, marginBottom: 8 }} value={data.address} onChange={e => upd({ address: e.target.value })} placeholder="Gadenavn og nummer" />
        <input style={inputStyle} value={data.address2} onChange={e => upd({ address2: e.target.value })} placeholder="Adresselinje 2 (valgfrit)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 160px', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Postnr</div>
          <input style={inputStyle} value={data.postnr} onChange={e => upd({ postnr: e.target.value })} placeholder="1234" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>By <span style={{ color: '#dc2626' }}>*</span></div>
          <input style={inputStyle} value={data.by} onChange={e => upd({ by: e.target.value })} placeholder="By" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Land</div>
          <select style={{ ...inputStyle, background: '#fff' }} value={data.land} onChange={e => upd({ land: e.target.value })}>
            {LANDE.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #f3f4f6', marginBottom: 12, paddingTop: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Kontraktoplysninger
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>
            CPR-nummer
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>Behandles fortroligt</span>
          </div>
          <input
            style={inputStyle}
            type="password"
            value={data.cpr}
            onChange={e => upd({ cpr: e.target.value })}
            placeholder="DDMMÅÅ-XXXX"
            autoComplete="off"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Reg.nr.</div>
            <input style={inputStyle} value={data.reg_nr} onChange={e => upd({ reg_nr: e.target.value })} placeholder="0000" />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Kontonr.</div>
            <input style={inputStyle} value={data.konto_nr} onChange={e => upd({ konto_nr: e.target.value })} placeholder="0000000000" />
          </div>
        </div>
      </div>
    </div>
  )
}

interface Props {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
  renters: Renter[]
}

export function SectionLejer({ state, onChange, renters }: Props) {
  const selected = renters.find(r => r.id === state.renter_id)
  const mode = state.lejer_mode ?? 'select'

  const modes = [
    { value: 'select', label: 'Vælg eksisterende lejer', desc: 'Vælg fra din lejer-liste' },
    { value: 'indhent', label: 'Indhent oplysninger fra lejer', desc: 'Send anmodning til lejer' },
    { value: 'manuel', label: 'Indtast oplysninger manuelt', desc: 'Udfyld lejers oplysninger nu' },
  ] as const

  const lejereManuel = state.lejere_manuel ?? []

  // A-3: the tenant is often already added as a contact under the lejemål.
  // Offer those (the ones with an email) as a one-click prefill → no re-typing.
  const [leaseContacts, setLeaseContacts] = useState<LeaseContact[]>([])
  // One-shot fetch keyed on lease_id; the guard clause resets to empty when
  // there is no lease yet, mirroring the async fetch's own setLeaseContacts
  // calls below.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!state.lease_id) { setLeaseContacts([]); return }
    apiFetch<LeaseContact[]>(`/api/v1/leases/${state.lease_id}/contacts`)
      .then((cs) => setLeaseContacts(cs.filter((c) => c.email)))
      .catch(() => setLeaseContacts([]))
  }, [state.lease_id])

  function addLejerFromContact(c: LeaseContact) {
    const parts = (c.contact_person || '').trim().split(/\s+/)
    const prefilled: LejerManuel = {
      ...EMPTY_LEJER,
      firstname: parts[0] || '',
      lastname: parts.slice(1).join(' '),
      email: c.email || '',
      phone: c.phone || '',
    }
    const cur = state.lejere_manuel ?? []
    const onlyEmpty = cur.length <= 1 && !cur[0]?.firstname && !cur[0]?.email
    onChange({ lejere_manuel: onlyEmpty ? [prefilled] : [...cur, prefilled] })
  }

  function updateLejer(idx: number, patch: Partial<LejerManuel>) {
    const next = lejereManuel.map((l, i) => i === idx ? { ...l, ...patch } : l)
    onChange({ lejere_manuel: next })
  }

  function addLejer() {
    onChange({ lejere_manuel: [...lejereManuel, { ...EMPTY_LEJER }] })
  }

  function removeLejer(idx: number) {
    onChange({ lejere_manuel: lejereManuel.filter((_, i) => i !== idx) })
  }

  // Ensure at least one lejer card exists in manuel mode
  const displayLejere = lejereManuel.length === 0 && mode === 'manuel'
    ? [{ ...EMPTY_LEJER }]
    : lejereManuel

  // Indhent mode reuses lejere_manuel[0] for first/last name (email lives in indhent_email).
  const indhentLejer = lejereManuel[0] ?? EMPTY_LEJER
  function updateIndhentLejer(patch: Partial<LejerManuel>) {
    const base = lejereManuel.length ? lejereManuel : [{ ...EMPTY_LEJER }]
    const next = base.map((l, i) => i === 0 ? { ...l, ...patch } : l)
    onChange({ lejere_manuel: next })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Mode selector ────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconPerson />} title="Lejer" subtitle="Hvem skal leje lejemålet?">

        {/* 3-column mode picker */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
          {modes.map(opt => (
            <div
              key={opt.value}
              onClick={() => {
                onChange({ lejer_mode: opt.value })
                // Seed first lejer card when switching to manuel
                if (opt.value === 'manuel' && lejereManuel.length === 0) {
                  onChange({ lejer_mode: opt.value, lejere_manuel: [{ ...EMPTY_LEJER }] })
                }
              }}
              style={{
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                border: `1px solid ${mode === opt.value ? BLUE : '#d1d5db'}`,
                background: mode === opt.value ? '#eef2ff' : 'white',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: mode === opt.value ? BLUE : '#111827', marginBottom: 3 }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{opt.desc}</div>
            </div>
          ))}
        </div>

        {/* ── Select existing renter ── */}
        {mode === 'select' && (
          <>
            {renters.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', borderRadius: 10, border: '1px dashed #d1d5db', background: '#fafafa' }}>
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>Ingen lejere fundet</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Opret en lejer under Lejere, eller brug "Indhent oplysninger"</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {renters.map(r => (
                  <div
                    key={r.id}
                    onClick={() => onChange({ renter_id: r.id })}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 7, cursor: 'pointer',
                      border: `1px solid ${state.renter_id === r.id ? BLUE : '#d1d5db'}`,
                      background: state.renter_id === r.id ? '#eef2ff' : 'white',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: state.renter_id === r.id ? BLUE : '#111827' }}>
                        {r.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.email}</div>
                    </div>
                    {state.renter_id === r.id && (
                      <div style={{ color: BLUE, fontSize: 18, fontWeight: 700 }}>✓</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {selected && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', marginTop: 8, fontSize: 13, color: '#166534' }}>
                {selected.name} modtager kontrakten til signering på {selected.email}
              </div>
            )}
          </>
        )}

        {/* ── Indhent oplysninger ── */}
        {mode === 'indhent' && (
          <>
            <div style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 7, padding: '12px 14px', marginBottom: 12, fontSize: 12, color: '#4338ca', lineHeight: 1.6 }}>
              Lejeren modtager en e-mail med et link til at udfylde sine personlige oplysninger. Oplysningerne flettes automatisk ind i kontrakten, inden den sendes til underskrift. Lejeren behøver ikke have en profil i Boligflow i forvejen.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Fornavn <span style={{ color: '#dc2626' }}>*</span></div>
                <input style={inputStyle} value={indhentLejer.firstname}
                  onChange={e => updateIndhentLejer({ firstname: e.target.value })} placeholder="Fornavn" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Efternavn <span style={{ color: '#dc2626' }}>*</span></div>
                <input style={inputStyle} value={indhentLejer.lastname}
                  onChange={e => updateIndhentLejer({ lastname: e.target.value })} placeholder="Efternavn" />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>Lejerens e-mail <span style={{ color: '#dc2626' }}>*</span></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={{ ...inputStyle, flex: 1 }}
                  type="email"
                  placeholder="lejer@email.dk"
                  value={state.indhent_email ?? ''}
                  onChange={e => onChange({ indhent_email: e.target.value })}
                />
                <button
                  type="button"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '9px 16px', borderRadius: 8, border: 'none',
                    background: BLUE, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <IconSend />
                  Send
                </button>
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                Aktiveres i næste version — notér lejerens e-mail for nu.
              </div>
            </div>
          </>
        )}

        {/* ── Indtast manuelt ── */}
        {mode === 'manuel' && (
          <>
            {leaseContacts.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>
                  Fra lejemålets kontakter
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>Klik for at udfylde med en kontakt</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {leaseContacts.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => addLejerFromContact(c)}
                      style={{
                        padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
                        border: '1px solid #c7d2fe', background: '#eef2ff', color: BLUE,
                        fontSize: 12, fontWeight: 500,
                      }}
                    >
                      + {c.contact_person || c.email}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {displayLejere.map((lejer, idx) => (
              <ManuelLejerForm
                key={idx}
                num={idx + 1}
                data={lejer}
                onChange={d => {
                  if (lejereManuel.length === 0) {
                    onChange({ lejere_manuel: [d] })
                  } else {
                    updateLejer(idx, d)
                  }
                }}
                onRemove={idx > 0 ? () => removeLejer(idx) : undefined}
              />
            ))}

            <button
              type="button"
              onClick={addLejer}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '12px 16px', borderRadius: 10,
                border: '1.5px dashed #d1d5db', background: '#fafafa',
                cursor: 'pointer', fontSize: 13, color: '#6b7280',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconPersonAdd />
                Tilføj en lejer mere
              </div>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: BLUE, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 600 }}>
                +
              </div>
            </button>
          </>
        )}

        {/* ── Nuværende adresse (kontrakt pg 1) ── */}
        <div style={{ borderTop: '1px solid #f3f4f6', marginTop: 8, paddingTop: 14 }}>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 6 }}>
            Nuværende adresse
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>Bruges på kontraktens forside</span>
          </div>
          <input
            style={inputStyle}
            type="text"
            placeholder="Vejnavn 1, 2. tv, 2100 København Ø"
            value={state.lejer_address || ''}
            onChange={e => onChange({ lejer_address: e.target.value })}
          />
        </div>
      </SectionBlock>

      {/* ── Underskrift og levering ───────────────────────────────────────── */}
      <SectionBlock icon={<IconPerson />} title="Underskrift og levering" subtitle="Hvordan underskrives og leveres kontrakten?">
        <Toggle
          label="MitID-underskrift"
          value={state.mitid_signing ?? true}
          onChange={v => onChange({ mitid_signing: v })}
        />
        <Toggle
          label="Engelsk oversættelse af kontrakt"
          value={state.english_translation ?? false}
          onChange={v => onChange({ english_translation: v })}
        />
        <Toggle
          label="SMS-notifikation til lejer"
          value={false}
          onChange={() => {}}
          disabled
          comingSoon
        />
      </SectionBlock>

    </div>
  )
}
